/**
 * Single aggregated startup request — get_homepage_data Cloud Function.
 * Cache-first: render localStorage immediately, hydrate from one response.
 */
import { auth } from '../auth/firebaseAuth';
import { readCache, writeCache } from '../utils/dataCache';
import { saveCachedProfile } from '../utils/profileCache';
import { mergeHomepageSections } from '../utils/homepageHydration';
import { perfMarkStart, perfMarkEnd } from '../utils/startupPerf';

const HOMEPAGE_CACHE_KEY = 'lockedin_homepage_v1';

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

/** @returns {object | null} */
export function loadHomepageCacheSync() {
  const { data } = readCache(HOMEPAGE_CACHE_KEY);
  return data ?? null;
}

function persistHomepagePayload(payload, uid) {
  const merged = mergeHomepageSections(payload);
  writeCache(HOMEPAGE_CACHE_KEY, merged);
  // NOTE: intentionally does NOT write GAMES_CACHE_KEY (lockedin_games_v1).
  // `merged.upcomingGames` is the lockable-upcoming subset only; writing it
  // here clobbered the full games snapshot (live + final rows) that
  // persistGamesCache stores, starving the Live Now strip and challenge score
  // sync. persistGamesCache is the sole writer of the games cache.
  if (uid && merged?.profile) {
    saveCachedProfile(uid, merged.profile);
  }
}

/**
 * One aggregated fetch — games + wallet + subscription + profile.
 * @param {string | null} idToken
 */
export async function fetchHomepageData(idToken = null) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured');

  perfMarkStart('api.getHomepageData');
  const headers = { Accept: 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  try {
    const res = await fetch(`${base}/get_homepage_data`, { method: 'GET', headers });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(err || `get_homepage_data ${res.status}`);
    }
    const payload = await res.json();
    perfMarkEnd('api.getHomepageData', 'api', {
      gameCount: payload?.upcomingGames?.length ?? 0,
      hasProfile: Boolean(payload?.profile),
    });
    return payload;
  } catch (e) {
    perfMarkEnd('api.getHomepageData', 'api', { ok: false, error: e?.message });
    throw e;
  }
}

export async function fetchAndPersistHomepage(uid) {
  let idToken = null;
  if (uid && auth.currentUser) {
    idToken = await auth.currentUser.getIdToken();
  }
  const payload = await fetchHomepageData(idToken);
  persistHomepagePayload(payload, uid);
  return payload;
}

/** Remove aggregated homepage cache — call on guest entry / logout. */
export function clearHomepageCache() {
  try {
    window.localStorage.removeItem(HOMEPAGE_CACHE_KEY);
  } catch (_) {}
}

export { persistHomepagePayload, HOMEPAGE_CACHE_KEY };
